import { API_URLS } from "../Constants/api.constants";
import api from "./api";
import { errorHandling } from "./error.handling";

const isFilterable = (value) =>
  value !== undefined && value !== null && value !== "" && value !== "TODOS";

async function fetchProtocols(filters) {
  try {
    const params = {
      StartDate: filters?.StartDate ?? "",
      EndDate: filters?.EndDate ?? "",
    };
    if (isFilterable(filters?.LicensePlate)) params.LicensePlate = filters.LicensePlate;
    if (isFilterable(filters?.ClientName)) params.ClientName = filters.ClientName;
    if (isFilterable(filters?.ProtocolId)) params.ProtocolId = filters.ProtocolId;
    if (isFilterable(filters?.ProtocolSubTypeId)) params.ProtocolSubTypeId = filters.ProtocolSubTypeId;
    if (isFilterable(filters?.ProtocolTypeId)) params.ProtocolTypeId = filters.ProtocolTypeId;
    if (isFilterable(filters?.ProtocolStatusId)) params.ProtocolStatusId = filters.ProtocolStatusId;

    const queryParams = new URLSearchParams(params);

    const response = await api.get(
      `${API_URLS.PROTOCOL_STATUS_BASE}?${queryParams.toString()}`
    );

    const treated = errorHandling.handleResponse(
      response,
      "protocolService.fetchProtocols"
    );
    if (treated?.error) return null;
    return Array.isArray(treated) ? treated : [];
  } catch (error) {
    errorHandling.cathError(error, "protocolService.fetchProtocols");
    return null;
  }
}

async function fetchProtocolTypes() {
  try {
    const response = await api.get(API_URLS.PROTOCOL_TYPES);

    const treated = errorHandling.handleResponse(
      response,
      "protocolService.fetchProtocolTypes"
    );
    return Array.isArray(treated) ? treated : [];
  } catch (error) {
    errorHandling.cathError(error, "protocolService.fetchProtocolTypes");
    return [];
  }
}

// Nome do query param assumido (não documentado na especificação original),
// seguindo a convenção usada em GET /protocols (ProtocolTypeId).
async function fetchProtocolSubtypes(protocolTypeId) {
  try {
    const queryParams = new URLSearchParams({ ProtocolTypeId: protocolTypeId ?? "" });

    const response = await api.get(
      `${API_URLS.PROTOCOL_SUBTYPES}?${queryParams.toString()}`
    );

    const treated = errorHandling.handleResponse(
      response,
      "protocolService.fetchProtocolSubtypes"
    );
    return Array.isArray(treated) ? treated : [];
  } catch (error) {
    errorHandling.cathError(error, "protocolService.fetchProtocolSubtypes");
    return [];
  }
}

async function fetchProtocolStatusList() {
  try {
    const response = await api.get(API_URLS.PROTOCOL_ALERTS_STATUS);

    const treated = errorHandling.handleResponse(
      response,
      "protocolService.fetchProtocolStatusList"
    );
    return Array.isArray(treated) ? treated : [];
  } catch (error) {
    errorHandling.cathError(error, "protocolService.fetchProtocolStatusList");
    return [];
  }
}

async function fetchProtocolInteractions({ platformName, protocolId }) {
  try {
    const queryString = new URLSearchParams({
      PlatformName: platformName ?? "",
      ProtocolId: protocolId ?? "",
    });

    const response = await api.get(
      `${API_URLS.PROTOCOL_ALERTS_DETAILS}?${queryString.toString()}`
    );

    const treated = errorHandling.handleResponse(
      response,
      "protocolService.fetchProtocolInteractions"
    );
    return Array.isArray(treated) ? treated : [];
  } catch (error) {
    errorHandling.cathError(error, "protocolService.fetchProtocolInteractions");
    return [];
  }
}

async function createProtocol(payload) {
  try {
    const response = await api.post(API_URLS.PROTOCOL_STATUS_BASE, payload);

    const treated = errorHandling.handleResponse(
      response,
      "protocolService.createProtocol"
    );
    if (treated?.error) return treated;
    return treated ?? true;
  } catch (error) {
    errorHandling.cathError(error, "protocolService.createProtocol");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      validations: error?.response?.data?.validations,
    };
  }
}

async function updateProtocol(protocolId, payload) {
  try {
    const response = await api.put(
      `${API_URLS.PROTOCOL_STATUS_BASE}/${protocolId}`,
      payload
    );

    const treated = errorHandling.handleResponse(
      response,
      "protocolService.updateProtocol"
    );
    if (treated?.error) return treated;
    return treated ?? true;
  } catch (error) {
    errorHandling.cathError(error, "protocolService.updateProtocol");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      validations: error?.response?.data?.validations,
    };
  }
}

async function updateProtocolStatus({ protocolId, platformName, protocolStatusId }) {
  try {
    const response = await api.patch(
      `${API_URLS.PROTOCOL_STATUS_BASE}/${protocolId}/status`,
      { platformName, protocolStatusId }
    );

    return response?.status >= 200 && response.status < 300;
  } catch (error) {
    errorHandling.cathError(error, "protocolService.updateProtocolStatus");
    return false;
  }
}

async function fetchProtocolSummary(regionalId) {
  try {
    const params = new URLSearchParams({
      regionalId: isFilterable(regionalId) ? regionalId : 0,
    });

    const response = await api.get(`${API_URLS.PROTOCOL_SUMMARY}?${params.toString()}`);

    const treated = errorHandling.handleResponse(
      response,
      "protocolService.fetchProtocolSummary"
    );
    if (treated?.error) return null;
    return Array.isArray(treated) ? treated : [];
  } catch (error) {
    errorHandling.cathError(error, "protocolService.fetchProtocolSummary");
    return null;
  }
}

async function updateProtocolReminder({ protocolId, rememberDate }) {
  try {
    const response = await api.patch(API_URLS.PROTOCOL_REMINDER, {
      protocolId,
      rememberDate,
    });

    return response?.status >= 200 && response.status < 300;
  } catch (error) {
    errorHandling.cathError(error, "protocolService.updateProtocolReminder");
    return false;
  }
}

export const protocolService = {
  fetchProtocols,
  fetchProtocolTypes,
  fetchProtocolSubtypes,
  fetchProtocolStatusList,
  fetchProtocolInteractions,
  createProtocol,
  updateProtocol,
  updateProtocolStatus,
  updateProtocolReminder,
  fetchProtocolSummary,
};
